import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { FiEdit2, FiTrash2 } from "react-icons/fi";

import GenericHeader from "../../../Componentes/GenericHeader";
import SortableTable from "../../../Componentes/Utils/SortableTable";
import FilterContainer from "../../../Componentes/Utils/FilterContainer";
import Filters from "../../../Componentes/Utils/Filters";
import MultiselectCheck from "../../../Componentes/Utils/MultiselectCheck";
import { formatarData } from "../../../Funcoes/FormatarData";
import { formatCPF } from "../../../Funcoes/FormatCPF";
import useApi from "../../../hooks/useApi";
import { useModal } from "../../../contexts/ModalContext";
import { AuthContext } from "../../../Auth/AuthProvider";
import { VerifyPermission } from "../../../Funcoes/VerifyPermission";

import ModalEditarAtestado from "./ModalEditarAtestado";
import ModalDeleteAtestado from "./ModalDeleteAtestado";

const PAGE_SIZE = 100;
const SCROLL_THRESHOLD = 40;
const PERMISSAO_BASE = { modulo: "ssma", submodulo: "atendimento-amb" };

const PageContent = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
    box-sizing: border-box;
    overflow: hidden;
`;

const Statistics = styled.div`
    width: 240px;
    flex-shrink: 0;
    background-color: #f3f3f3;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 12px;
    padding: 10px;
    box-sizing: border-box;

    @media (max-width: 768px) {
        width: 100%;
    }
`;

const ContainerStatistic = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    justify-content: center;
    min-height: 100%;
`;

const TituloStatistic = styled.h3`
    text-align: center;
    margin: 0;
    padding: 0;
    font-size: 13px;
    font-weight: bold;
    color: #000;
`;

const TextoStatistic = styled.p`
    margin: 0;
    padding: 0;
    font-size: 13px;
    color: #333;

    span { font-weight: 700; }
`;

const TableFlexFill = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;

    & > div {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }
`;

const AcoesCelula = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
`;

const BotaoAcao = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    color: ${(p) => (p.$danger ? "#b91c1c" : "#1d4ed8")};
    background-color: ${(p) =>
        p.$danger ? "rgba(185, 28, 28, 0.12)" : "rgba(29, 78, 216, 0.10)"};
    border: 1px solid transparent;

    &:hover {
        background-color: ${(p) =>
            p.$danger ? "rgba(185, 28, 28, 0.22)" : "rgba(29, 78, 216, 0.20)"};
        border-color: ${(p) =>
            p.$danger ? "rgba(185, 28, 28, 0.5)" : "rgba(29, 78, 216, 0.45)"};
    }
`;

function formatarDataAtestado(value) {
    if (!value) return "-";
    try { return formatarData(value); } catch { return String(value); }
}

function startOfDayMs(dateStr) {
    if (!dateStr) return null;
    return new Date(`${dateStr}T00:00:00`).getTime();
}
function endOfDayMs(dateStr) {
    if (!dateStr) return null;
    return new Date(`${dateStr}T23:59:59.999`).getTime();
}

function toValueSet(selected) {
    if (!Array.isArray(selected) || selected.length === 0) return null;
    return new Set(selected.map((s) => (typeof s === "object" ? s?.value : s)));
}

function uniqueSortedStrings(values) {
    return [...new Set(values.map((v) => String(v ?? "").trim()).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, "pt-BR", { sensitivity: "base" })
    );
}

function aplicarFiltros(lista, filtros) {
    const nome = filtros.nome.trim().toUpperCase();
    const cpf  = filtros.cpf.replace(/\D/g, "");
    const tDe  = startOfDayMs(filtros.dataDe);
    const tAte = endOfDayMs(filtros.dataAte);
    const funcoesSet = toValueSet(filtros.funcoes);
    const centrosSet = toValueSet(filtros.centrosCusto);
    const contratosSet = toValueSet(filtros.contratos);
    const lideresSet = toValueSet(filtros.lideres);

    return lista.filter((row) => {
        const rNome = String(row.nome ?? "").toUpperCase();
        const rCpf  = String(row.cpf ?? "");
        if (nome && !rNome.includes(nome)) return false;
        if (cpf && !rCpf.includes(cpf)) return false;
        if (funcoesSet && !funcoesSet.has(String(row.funcao ?? ""))) return false;
        if (centrosSet && !centrosSet.has(String(row.centro_custo ?? ""))) return false;
        if (contratosSet && !contratosSet.has(String(row.contrato ?? ""))) return false;
        if (lideresSet && !lideresSet.has(String(row.lider ?? ""))) return false;
        if (row.data_atestado) {
            const t = new Date(`${row.data_atestado}T00:00:00`).getTime();
            if (tDe != null && t < tDe) return false;
            if (tAte != null && t > tAte) return false;
        } else if (tDe != null || tAte != null) {
            return false;
        }
        return true;
    });
}

function calcularEstatisticas(lista) {
    let total = 0;
    let diasAbonados = 0;
    for (const row of lista) {
        total += 1;
        const dias = Number(row?.dias_abonados);
        if (Number.isFinite(dias)) diasAbonados += dias;
    }
    return { total, diasAbonados };
}

const AtestadosContent = forwardRef(function AtestadosContent({ refreshKey = 0 }, ref) {
    const api = useApi();
    const { user } = useContext(AuthContext);
    const { openModal, closeModal, isModalOpen } = useModal();
    const tableRef = useRef(null);

    const podeLancarEditar = useMemo(
        () => VerifyPermission(user, { ...PERMISSAO_BASE, funcao: "atestados-lancar-editar" }),
        [user]
    );
    const podeApagar = useMemo(
        () => VerifyPermission(user, { ...PERMISSAO_BASE, funcao: "atestados-apagar" }),
        [user]
    );

    const [loading, setLoading] = useState(true);
    const [atestados, setAtestados] = useState([]);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    const [nome, setNome] = useState("");
    const [cpf, setCpf] = useState("");
    const [dataDe, setDataDe] = useState("");
    const [dataAte, setDataAte] = useState("");
    const [selectedFuncoes, setSelectedFuncoes] = useState([]);
    const [selectedCentrosCusto, setSelectedCentrosCusto] = useState([]);
    const [selectedContratos, setSelectedContratos] = useState([]);
    const [selectedLideres, setSelectedLideres] = useState([]);

    const [atestadoParaEditar, setAtestadoParaEditar] = useState(null);
    const [atestadoParaDelete, setAtestadoParaDelete] = useState(null);

    const filtros = useMemo(
        () => ({
            nome,
            cpf,
            dataDe,
            dataAte,
            funcoes: selectedFuncoes,
            centrosCusto: selectedCentrosCusto,
            contratos: selectedContratos,
            lideres: selectedLideres,
        }),
        [
            nome,
            cpf,
            dataDe,
            dataAte,
            selectedFuncoes,
            selectedCentrosCusto,
            selectedContratos,
            selectedLideres,
        ]
    );

    const filtered = useMemo(
        () => aplicarFiltros(atestados, filtros),
        [atestados, filtros]
    );

    useImperativeHandle(ref, () => ({
        getFilteredAtestados: () => filtered,
    }), [filtered]);

    const estatisticas = useMemo(() => calcularEstatisticas(filtered), [filtered]);

    const opcoesFuncaoMs = useMemo(
        () => uniqueSortedStrings(atestados.map((r) => r.funcao)).map((v) => ({ value: v, label: v })),
        [atestados]
    );
    const opcoesCentroCustoMs = useMemo(
        () => uniqueSortedStrings(atestados.map((r) => r.centro_custo)).map((v) => ({ value: v, label: v })),
        [atestados]
    );
    const opcoesContratoMs = useMemo(
        () => uniqueSortedStrings(atestados.map((r) => r.contrato)).map((v) => ({ value: v, label: v })),
        [atestados]
    );
    const opcoesLiderMs = useMemo(
        () => uniqueSortedStrings(atestados.map((r) => r.lider)).map((v) => ({ value: v, label: v })),
        [atestados]
    );

    const initialFetch = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/Painel/API/AtendimentoAmb/Atestados/List/index.php");
            setAtestados(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Erro ao listar atestados:", err);
            setAtestados([]);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        initialFetch();
    }, [initialFetch, refreshKey]);

    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
    }, [filtros]);

    const loadMoreRows = useCallback(() => {
        const total = filtered.length;
        setVisibleCount((prev) => {
            if (prev >= total) return prev;
            return Math.min(prev + PAGE_SIZE, total);
        });
    }, [filtered.length]);

    const abrirEdicao = (row) => {
        if (!podeLancarEditar) return;
        setAtestadoParaEditar(row);
        openModal("atestadoEdit");
    };
    const abrirDelete = (row) => {
        if (!podeApagar) return;
        setAtestadoParaDelete(row);
        openModal("atestadoDelete");
    };

    const fecharEdicao = () => { setAtestadoParaEditar(null); closeModal(); };
    const fecharDelete = () => { setAtestadoParaDelete(null); closeModal(); };

    const columns = useMemo(
        () => [
            { id: "id", header: "ID", accessor: "id", sortable: true, width: "70px", align: "center" },
            {
                id: "cpf",
                header: "CPF",
                accessor: "cpf",
                sortable: true,
                width: "140px",
                render: (v) => (v ? formatCPF(String(v)) : "-"),
            },
            { id: "nome", header: "NOME", accessor: "nome", sortable: true },
            { id: "funcao", header: "FUNÇÃO", accessor: "funcao", sortable: true },
            { id: "centro_custo", header: "CENTRO CUSTO", accessor: "centro_custo", sortable: true, width: "140px" },
            { id: "contrato", header: "CONTRATO", accessor: "contrato", sortable: true, width: "120px" },
            { id: "lider", header: "LÍDER", accessor: "lider", sortable: true, width: "140px" },
            {
                id: "data_atestado",
                header: "DATA",
                accessor: "data_atestado",
                sortable: true,
                width: "110px",
                render: (v) => formatarDataAtestado(v),
                sortValue: (row) => row.data_atestado ?? "",
            },
            { id: "dias_abonados", header: "DIAS", accessor: "dias_abonados", sortable: true, width: "70px", align: "center" },
            { id: "cid", header: "CID", accessor: "cid", sortable: true, width: "100px" },
            { id: "nome_medico", header: "MÉDICO", accessor: "nome_medico", sortable: true },
            ...(podeLancarEditar || podeApagar
                ? [{
                    id: "acoes",
                    header: "AÇÕES",
                    sortable: false,
                    width: "90px",
                    align: "center",
                    render: (_, row) => (
                        <AcoesCelula>
                            {podeLancarEditar && (
                                <BotaoAcao
                                    title="Editar"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        abrirEdicao(row);
                                    }}
                                >
                                    <FiEdit2 size={14} />
                                </BotaoAcao>
                            )}
                            {podeApagar && (
                                <BotaoAcao
                                    $danger
                                    title="Remover"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        abrirDelete(row);
                                    }}
                                >
                                    <FiTrash2 size={14} />
                                </BotaoAcao>
                            )}
                        </AcoesCelula>
                    ),
                }]
                : []),
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [podeLancarEditar, podeApagar]
    );

    return (
        <>
            <PageContent>
                <GenericHeader responsive>
                    <Filters columns={4}>
                        <FilterContainer
                            type="text"
                            placeholder="NOME COLABORADOR"
                            valor={nome}
                            changeFunc={setNome}
                        />
                        <FilterContainer
                            type="text"
                            placeholder="CPF"
                            valor={cpf}
                            changeFunc={setCpf}
                            maxLength={14}
                        />
                        <MultiselectCheck
                            options={opcoesFuncaoMs}
                            selectedOptions={selectedFuncoes}
                            setSelectedOptions={setSelectedFuncoes}
                            placeholder="FUNÇÃO..."
                        />
                        <MultiselectCheck
                            options={opcoesCentroCustoMs}
                            selectedOptions={selectedCentrosCusto}
                            setSelectedOptions={setSelectedCentrosCusto}
                            placeholder="CENTRO CUSTO..."
                        />
                        <MultiselectCheck
                            options={opcoesContratoMs}
                            selectedOptions={selectedContratos}
                            setSelectedOptions={setSelectedContratos}
                            placeholder="CONTRATO..."
                        />
                        <MultiselectCheck
                            options={opcoesLiderMs}
                            selectedOptions={selectedLideres}
                            setSelectedOptions={setSelectedLideres}
                            placeholder="LÍDER..."
                        />
                        <FilterContainer
                            type="date"
                            placeholder="DATA — A PARTIR DE"
                            valor={dataDe}
                            changeFunc={setDataDe}
                        />
                        <FilterContainer
                            type="date"
                            placeholder="DATA — ATÉ"
                            valor={dataAte}
                            changeFunc={setDataAte}
                        />
                    </Filters>

                    <Statistics>
                        <ContainerStatistic>
                            <TituloStatistic>ESTATÍSTICAS</TituloStatistic>
                            <TextoStatistic>
                                ATESTADOS: <span>{estatisticas.total}</span>
                            </TextoStatistic>
                            <TextoStatistic>
                                DIAS ABONADOS: <span>{estatisticas.diasAbonados}</span>
                            </TextoStatistic>
                        </ContainerStatistic>
                    </Statistics>
                </GenericHeader>

                <TableFlexFill>
                    <SortableTable
                        ref={tableRef}
                        columns={columns}
                        data={filtered}
                        limit={visibleCount}
                        onScrollReachEnd={loadMoreRows}
                        scrollReachThreshold={SCROLL_THRESHOLD}
                        isLoading={loading}
                        loadingText="Carregando atestados..."
                        emptyText="Nenhum atestado encontrado."
                        initialSort={{ key: "data_atestado", direction: "desc" }}
                        getRowId={(row) => row.id}
                    />
                </TableFlexFill>
            </PageContent>

            {podeLancarEditar && isModalOpen("atestadoEdit") && atestadoParaEditar != null && (
                <ModalEditarAtestado
                    atestado={atestadoParaEditar}
                    fechar={fecharEdicao}
                    onSaved={() => { fecharEdicao(); initialFetch(); }}
                />
            )}

            {podeApagar && isModalOpen("atestadoDelete") && atestadoParaDelete != null && (
                <ModalDeleteAtestado
                    atestado={atestadoParaDelete}
                    fecharModal={fecharDelete}
                    onDeleted={initialFetch}
                />
            )}
        </>
    );
});

export default AtestadosContent;
